import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, CalendarDays, BadgeDollarSign, UserCheck, CheckCircle2 } from "lucide-react";
import Seo, { breadcrumbSchema, faqSchema, serviceSchema } from "../components/Seo";
import { Reveal, Eyebrow, BtnPrimary, BtnGhost, FaqList, CtaBand, Crumbs } from "../components/Shared";
import { LeadBand } from "../components/LeadForm";
import { CLASSES, chalkitLink } from "../data/mock";
import { getArticle } from "../data/articles";

export default function ClassDetail() {
  const { slug } = useParams();
  const cls = CLASSES.find((c) => c.slug === slug);
  if (!cls) return <Navigate to="/classes" replace />;

  const article = getArticle(cls.relatedArticle);
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Programs", path: "/classes" },
    { name: cls.name, path: `/classes/${cls.slug}` },
  ];

  return (
    <>
      <Seo
        title={`${cls.name} in West Roxbury | Boston Iron Grit`}
        description={cls.metaDescription || cls.answer.slice(0, 155)}
        path={`/classes/${cls.slug}`}
        schemas={[breadcrumbSchema(crumbs), serviceSchema(cls), faqSchema(cls.faqs)]}
      />

      {/* Hero */}
      <section className="relative bg-[#0A0A0B] overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-[center_30%]" style={{ backgroundImage: `url(${cls.image})` }} aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0B] via-[#0A0A0B]/85 to-[#0A0A0B]/35" aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-transparent to-transparent" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-20 lg:pt-20 lg:pb-28">
          <Reveal>
            <Crumbs items={crumbs} />
            {cls.badge && (
              <img
                src={cls.badge}
                alt={`${cls.name} logo`}
                data-testid="class-badge-logo"
                className="mt-8 w-52 h-auto border border-white/15 shadow-[0_10px_30px_rgba(0,0,0,0.5)]"
                loading="lazy"
              />
            )}
            <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.3em] text-[#9D0423]">// {cls.heroLabel}</p>
            <h1 className="mt-4 font-heading font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95] tracking-tight max-w-3xl">
              {cls.name} <span className="text-neutral-500">in West Roxbury</span>
            </h1>
            {/* AEO answer box */}
            <p className="mt-7 text-neutral-200 text-base lg:text-lg leading-relaxed max-w-2xl border-l-2 border-[#9D0423] pl-5">
              {cls.answer}
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <BtnPrimary to="/get-started">Book a Free Goal Review</BtnPrimary>
              <BtnGhost href={chalkitLink(cls.chalkitKey)}>Join via ChalkIt Pro</BtnGhost>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Quick facts bar */}
      <section className="bg-[#0D0D0F] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 grid sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-white/5">
          {[
            { icon: CalendarDays, label: "Schedule", value: cls.scheduleNote },
            { icon: BadgeDollarSign, label: "Pricing", value: cls.priceNote },
            { icon: UserCheck, label: "Coached by", value: cls.coachName },
          ].map((f) => (
            <div key={f.label} className="flex items-start gap-4 py-6 px-6 first:pl-0">
              <f.icon size={18} className="text-[#9D0423] mt-0.5 shrink-0" />
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-neutral-500">{f.label}</div>
                <div className="mt-1 text-sm text-neutral-200 leading-snug">{f.value}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Who it's for + session breakdown */}
      <section className="bg-[#0A0A0B] py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16">
          <Reveal>
            <Eyebrow>Who it's for</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-3xl lg:text-4xl text-white tracking-tight leading-tight">
              This program fits you if…
            </h2>
            <ul className="mt-8 space-y-4">
              {cls.whoFor.map((w) => (
                <li key={w} className="flex items-start gap-3">
                  <CheckCircle2 size={18} className="text-[#9D0423] mt-0.5 shrink-0" />
                  <span className="text-[15px] text-neutral-300 leading-relaxed">{w}</span>
                </li>
              ))}
            </ul>
            {article && (
              <div className="mt-10 p-6 bg-[#121214] border border-white/5">
                <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-neutral-500 mb-2">From the blog</p>
                <Link to={`/blog/${article.slug}`} className="group">
                  <h3 className="text-white font-semibold leading-snug group-hover:text-[#E56A79] transition-colors">{article.title}</h3>
                  <span className="mt-2 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.16em] text-[#9D0423]">
                    Read the guide <ArrowRight size={13} />
                  </span>
                </Link>
              </div>
            )}
          </Reveal>

          <Reveal delay={120}>
            <Eyebrow>Inside a session</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-3xl lg:text-4xl text-white tracking-tight leading-tight">
              What a typical session includes
            </h2>
            <div className="mt-8 space-y-px">
              {cls.session.map((s, i) => (
                <div key={s.name} className="group flex items-start gap-5 p-5 bg-[#121214] border border-white/5 hover:border-[#9D0423]/40 transition-colors">
                  <div className="font-heading font-extrabold text-2xl text-white/15 group-hover:text-[#9D0423]/60 transition-colors w-10 shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between gap-4">
                      <h3 className="font-heading font-bold uppercase text-base text-white tracking-tight">{s.name}</h3>
                      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-500 shrink-0">{s.mins}</span>
                    </div>
                    <p className="mt-1.5 text-[13px] text-neutral-400 leading-relaxed">{s.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQs */}
      <section className="bg-[#0D0D0F] border-y border-white/5 py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-6">
          <Reveal>
            <FaqList faqs={cls.faqs} title={`${cls.name} — FAQs`} />
          </Reveal>
          <div className="mt-12 flex flex-col sm:flex-row gap-4">
            <BtnPrimary to="/get-started">Start With a Free Goal Review</BtnPrimary>
            <BtnGhost to="/pricing">See Pricing</BtnGhost>
          </div>
        </div>
      </section>

      {/* Other programs */}
      <section className="bg-[#0A0A0B] py-20">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <div className="flex items-end justify-between mb-10">
              <h2 className="font-heading font-extrabold uppercase text-3xl text-white tracking-tight">More programs</h2>
              <Link to="/classes" className="font-mono text-[12px] uppercase tracking-[0.16em] text-neutral-400 hover:text-white transition-colors">
                View all →
              </Link>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-3 gap-5">
            {CLASSES.filter((c) => c.slug !== cls.slug)
              .slice(0, 3)
              .map((c, i) => (
                <Reveal key={c.slug} delay={i * 80}>
                  <Link to={`/classes/${c.slug}`} className="group relative block h-[220px] overflow-hidden bg-[#121214]">
                    <img src={c.image} alt={c.name} className="absolute inset-0 w-full h-full object-cover object-[center_25%] opacity-50 group-hover:opacity-70 group-hover:scale-105 transition-all duration-700" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-5">
                      <h3 className="font-heading font-bold uppercase text-lg text-white tracking-tight">{c.name}</h3>
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#E56A79]">{c.heroLabel}</span>
                    </div>
                  </Link>
                </Reveal>
              ))}
          </div>
        </div>
      </section>

      <LeadBand
        formName={`class-${cls.slug}`}
        programInterest={cls.name}
        title={`Interested in ${cls.name}?`}
        sub="Tell us a little about you and a coach will be in touch to help you get started — usually the same day."
      />

      <CtaBand />
    </>
  );
}
